/* VIZZYO product videos: one owner per custom section; Digital/Swiper stay unchanged. */
(function () {
  'use strict';
  if (window.VzyProductVideos) {
    window.VzyProductVideos.mount(document);
    return;
  }

  const selector = 'template[data-vzy-product-videos]';
  const controllers = new Map();
  const pending = new Map();
  let openController = null;

  function play(video) {
    const result = video.play();
    if (result && result.catch) result.catch(function () { /* Browser may require another gesture. */ });
  }

  class ProductVideos {
    constructor(template, modalTemplate, anchor) {
      this.template = template;
      this.id = template.dataset.sectionId;
      this.owner = template.closest('.shopify-section') || template.parentElement;
      this.cleanups = [];
      this.visible = new Set();
      this.inerted = [];
      this.isOpen = false;
      this.destroyed = false;
      this.activeIndex = 0;
      this.resumeModal = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.preview = template.content.firstElementChild.cloneNode(true);
      this.modal = modalTemplate.content.firstElementChild.cloneNode(true);
      anchor.insertAdjacentElement(template.dataset.position, this.preview);
      document.body.appendChild(this.modal);
      this.sliderRoot = this.preview.querySelector('.video-slider-' + this.id);
      this.modalRoot = this.modal.querySelector('.video-modal-slider-' + this.id);
      this.closeButton = this.modal.querySelector('.video-modal-close-' + this.id);
      this.triggers = Array.from(this.preview.querySelectorAll('[data-video-trigger]'));
      this.slides = Array.from(this.modal.querySelectorAll('.video-modal-slide-' + this.id));
      this.previewMedia = Array.from(this.preview.querySelectorAll('video, iframe'));
      this.bind();
      this.previewSwiper = new window.Swiper(this.sliderRoot, {
        speed: this.motion.matches ? 0 : 300,
        slidesPerView: 'auto',
        navigation: {
          nextEl: this.preview.querySelector('.video-btn-next-' + this.id),
          prevEl: this.preview.querySelector('.video-btn-prev-' + this.id)
        },
        breakpoints: {
          320: { spaceBetween: Number(template.dataset.gapMobile) },
          768: { spaceBetween: Number(template.dataset.gapMobile) },
          1024: { spaceBetween: Number(template.dataset.gap) }
        }
      });
      this.modalSwiper = new window.Swiper(this.modalRoot, {
        speed: this.motion.matches ? 0 : 300,
        slidesPerView: 1,
        allowTouchMove: false,
        spaceBetween: 20,
        navigation: {
          nextEl: this.modal.querySelector('.video-modal-next-' + this.id),
          prevEl: this.modal.querySelector('.video-modal-prev-' + this.id)
        }
      });
      this.modalSwiper.on('slideChange', () => {
        this.activeIndex = this.modalSwiper.realIndex ?? this.modalSwiper.activeIndex;
        this.syncModal(true);
      });
      if (typeof window.IntersectionObserver === 'function') {
        this.observer = new window.IntersectionObserver(entries => {
          if (this.destroyed) return;
          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.1) this.visible.add(entry.target);
            else this.visible.delete(entry.target);
          });
          this.syncPreviews();
        }, { threshold: [0, 0.1] });
        this.triggers.forEach(trigger => this.observer.observe(trigger));
      }
      // No IntersectionObserver: static posters remain usable explicit modal triggers.
      this.syncModal(false);
      this.syncPreviews();
    }

    on(target, type, listener) {
      target.addEventListener(type, listener);
      this.cleanups.push(() => target.removeEventListener(type, listener));
    }

    bind() {
      this.triggers.forEach((trigger, index) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.modal.id);
        this.on(trigger, 'keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            trigger.click();
          }
        });
        this.on(trigger, 'click', () => this.open(index, trigger));
      });
      this.slides.forEach(slide => {
        const video = slide.querySelector('video');
        const button = slide.querySelector('.video-modal-action-' + this.id);
        if (video && button) {
          this.on(button, 'click', () => this.setSound(slide, video.muted));
          this.on(video, 'volumechange', () => this.reflectSound(slide));
        }
      });
      this.on(this.closeButton, 'click', () => this.close());
      this.on(this.modal, 'click', event => { if (event.target === this.modal) this.close(); });
      // Swiper can blur its Next button when it becomes disabled on the last clip.
      // Listen on document only while this dialog is open, so Escape/Tab still work.
      this.keydown = event => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'Tab') {
          const focusable = Array.from(this.modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, video[controls], [tabindex]:not([tabindex="-1"])'))
            .filter(element => !element.closest('[inert]') && !element.hidden && element.getClientRects().length);
          const first = focusable[0] || this.modal;
          const last = focusable[focusable.length - 1] || this.modal;
          const outside = !this.modal.contains(document.activeElement);
          if (event.shiftKey && (outside || document.activeElement === first || document.activeElement === this.modal)) {
            event.preventDefault(); last.focus();
          } else if (!event.shiftKey && (outside || document.activeElement === last || document.activeElement === this.modal)) {
            event.preventDefault(); first.focus();
          }
        }
      };
      const motionChanged = () => {
        [this.previewSwiper, this.modalSwiper].forEach(swiper => { if (swiper) swiper.params.speed = this.motion.matches ? 0 : 300; });
        this.syncPreviews();
      };
      if (this.motion.addEventListener) this.on(this.motion, 'change', motionChanged);
      else {
        this.motion.addListener(motionChanged);
        this.cleanups.push(() => this.motion.removeListener(motionChanged));
      }
      this.on(document, 'visibilitychange', () => {
        this.syncPreviews();
        const active = this.slides[this.activeIndex];
        const video = active && active.querySelector('video');
        const frame = active && active.querySelector('iframe');
        if (document.hidden) {
          this.resumeModal = this.isOpen && !!((video && !video.paused) || (frame && frame.hasAttribute('src')));
          this.slides.forEach(slide => this.stopMedia(slide, false));
        } else if (this.isOpen && this.resumeModal) {
          this.resumeModal = false;
          this.startModalMedia(active, false);
        }
      });
    }

    loadVideo(video) {
      if (!video.hasAttribute('src') && video.dataset.src) {
        video.setAttribute('src', video.dataset.src);
        video.load();
      }
    }

    syncPreviews() {
      this.triggers.forEach(trigger => {
        const shouldPlay = !this.destroyed && !this.isOpen && !document.hidden && !this.motion.matches && this.visible.has(trigger);
        // The original loading pulse follows the same visibility/motion policy.
        trigger.style.animationPlayState = shouldPlay ? 'running' : 'paused';
        const video = trigger.querySelector('video');
        const frame = trigger.querySelector('iframe');
        if (video) {
          video.muted = true;
          if (shouldPlay) { this.loadVideo(video); if (video.paused) play(video); }
          else video.pause();
        }
        if (frame) {
          if (shouldPlay && !frame.hasAttribute('src')) frame.setAttribute('src', frame.dataset.src);
          else if (!shouldPlay && frame.hasAttribute('src')) frame.removeAttribute('src');
        }
      });
    }

    reflectSound(slide) {
      const video = slide.querySelector('video');
      const button = slide.querySelector('.video-modal-action-' + this.id);
      if (!video || !button) return;
      const enabled = !video.muted;
      button.classList.toggle('active', enabled);
      button.setAttribute('aria-pressed', String(enabled));
      button.setAttribute('aria-label', enabled ? 'Dezactivează sunetul' : 'Activează sunetul');
    }

    setSound(slide, enabled) {
      const video = slide.querySelector('video');
      if (video) video.muted = !enabled;
      this.reflectSound(slide);
    }

    stopMedia(slide, reset) {
      const video = slide.querySelector('video');
      const frame = slide.querySelector('iframe');
      if (video) {
        video.pause();
        if (reset && video.readyState > 0) video.currentTime = 0;
      }
      if (frame && frame.hasAttribute('src')) frame.removeAttribute('src');
    }

    startModalMedia(slide, enableSound) {
      if (!slide || !this.isOpen || document.hidden || this.destroyed) return;
      const video = slide.querySelector('video');
      const frame = slide.querySelector('iframe');
      if (video) {
        this.loadVideo(video);
        if (enableSound) this.setSound(slide, true);
        else this.reflectSound(slide);
        play(video);
      }
      if (frame && !frame.hasAttribute('src')) frame.setAttribute('src', frame.dataset.src);
    }

    syncModal(enableSound) {
      this.slides.forEach((slide, index) => {
        const active = this.isOpen && index === this.activeIndex;
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
        if (active) this.startModalMedia(slide, enableSound);
        else this.stopMedia(slide, false);
      });
    }

    surface(open) {
      this.modal.dispatchEvent(new CustomEvent('vzy:surface-state', {
        bubbles: true, composed: true,
        detail: { id: this.modal.id, type: 'dialog', open: open, trigger: this.lastFocused }
      }));
    }

    open(index, trigger) {
      if (this.destroyed || this.isOpen) return;
      if (openController && openController !== this) openController.close();
      openController = this;
      this.lastFocused = trigger || document.activeElement;
      this.overflowBefore = document.body.style.overflow;
      this.bodyHadClass = document.body.classList.contains('vzy-video-modal-open');
      this.modalSwiper.slideTo(index, 0);
      this.activeIndex = index;
      this.isOpen = true;
      document.addEventListener('keydown', this.keydown);
      this.modal.classList.add('active');
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.removeAttribute('inert');
      this.closeButton.removeAttribute('tabindex');
      document.body.classList.add('vzy-video-modal-open');
      document.body.style.overflow = 'hidden';
      this.inerted = Array.from(document.body.children).filter(element => element !== this.modal && !element.hasAttribute('inert'));
      this.inerted.forEach(element => element.setAttribute('inert', ''));
      this.syncPreviews();
      this.syncModal(true);
      this.surface(true);
      this.closeButton.focus({ preventScroll: true });
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      document.removeEventListener('keydown', this.keydown);
      this.resumeModal = false;
      this.modal.classList.remove('active');
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.setAttribute('inert', '');
      this.closeButton.setAttribute('tabindex', '-1');
      if (!this.bodyHadClass) document.body.classList.remove('vzy-video-modal-open');
      document.body.style.overflow = this.overflowBefore;
      this.inerted.forEach(element => element.removeAttribute('inert'));
      this.inerted = [];
      this.slides.forEach(slide => { this.stopMedia(slide, true); this.setSound(slide, false); });
      this.syncModal(false);
      this.surface(false);
      if (openController === this) openController = null;
      this.syncPreviews();
      if (restoreFocus && this.lastFocused && this.lastFocused.isConnected) this.lastFocused.focus({ preventScroll: true });
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.close(false);
      this.cleanups.forEach(cleanup => cleanup());
      this.observer?.disconnect();
      this.previewMedia.forEach(media => { if (media.tagName === 'VIDEO') media.pause(); else media.removeAttribute('src'); });
      this.slides.forEach(slide => this.stopMedia(slide, true));
      this.previewSwiper?.destroy(true, true);
      this.modalSwiper?.destroy(true, true);
      this.preview.remove();
      this.modal.remove();
      this.visible.clear();
    }
  }

  function initialize(template, attempt = 0) {
    const id = template.dataset.sectionId;
    const current = controllers.get(id);
    if (current && current.template === template) return;
    if (current) { current.destroy(); controllers.delete(id); }
    const queued = pending.get(id);
    if (queued) { window.clearTimeout(queued.timer); pending.delete(id); }
    if (!template.isConnected) return;
    const modalTemplate = document.getElementById('ss-product-video-modal-' + id);
    const anchor = document.querySelector('#ss-product-videos') || document.querySelector(template.dataset.anchor);
    if (!modalTemplate || !anchor || typeof window.Swiper !== 'function') {
      if (attempt < 20) pending.set(id, { template: template, timer: window.setTimeout(() => initialize(template, attempt + 1), 100) });
      return;
    }
    controllers.set(id, new ProductVideos(template, modalTemplate, anchor));
  }

  function mount(root) {
    if (document.readyState === 'loading') return;
    root.querySelectorAll(selector).forEach(template => initialize(template));
  }

  function unload(event) {
    const id = event.detail && event.detail.sectionId;
    controllers.forEach((controller, key) => {
      if (key === id || event.target === controller.owner || event.target.contains(controller.template)) {
        controller.destroy(); controllers.delete(key);
      }
    });
    pending.forEach((entry, key) => {
      if (key === id || event.target.contains(entry.template)) {
        window.clearTimeout(entry.timer); pending.delete(key);
      }
    });
  }

  window.VzyProductVideos = { mount: mount };
  document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', unload);
  mount(document);
}());
